import { switchSiteChatLanguage } from '../phrases';
import { SITE_CHAT_LANGS, type SiteChatColors, type SiteChatConfig, type SiteChatField, type SiteChatLang, type SiteChatTheme } from '../types';

/**
 * What a page may change in the widget for itself: `window.AXLChatSettings` (read when the script starts) and
 * `AXLChat('configure', …)` later. Applied over the channel's saved settings on this page only — never saved, never
 * sent anywhere. What the server owns (the lead form being required, limits, working hours, allowed sites, voice
 * messages) it keeps checking, so nothing here loosens it: `voice`, `rules` and `triggers` can only be switched off.
 */
export type SiteChatPageSettings = {
    /** `false`: no round button — the page opens the chat itself (`AXLChat('open')`). */
    launcher?: boolean;
    /** Open the panel once the widget is ready. Read at start only. */
    openOnLoad?: boolean;
    position?: 'left' | 'right';
    /** px from the side edge. */
    offsetX?: number;
    /** px from the bottom edge. */
    offsetY?: number;
    /** Panel width, px (desktop). */
    width?: number;
    /** The widget's z-index on the page. */
    zIndex?: number;
    theme?: SiteChatTheme;
    /** Texts the owner left at their defaults follow it, like the language switch in the settings. */
    lang?: SiteChatLang;
    colors?: Partial<SiteChatColors>;
    /** Picture on the round button and the thread avatar (http(s) URL). */
    logo?: string;
    title?: string;
    subtitleOnline?: string;
    subtitleOffline?: string;
    greeting?: string;
    cardText?: string;
    composerPlaceholder?: string;
    /** `true`: the button opens the thread, never the home screen. */
    skipHome?: boolean;
    emoji?: boolean;
    /** Only `false`: the server refuses voice messages on a channel that has them off. */
    voice?: false;
    /** `false`: the channel's «Where to show» (page rules, mobile switches) is not applied on this page. */
    rules?: false;
    /** `false`: the channel's triggers do not run on this page. */
    triggers?: false;
};

/** What `setVisitor` takes for one field: text, a number, yes/no, a date, or several list options. */
export type SiteChatVisitorValue = string | number | boolean | string[];

/**
 * Prefills the lead form; the visitor still sends it (`AXLChat('setVisitor', …)`). `name`, `email`, `phone` fill the
 * built-in fields; any other key is a contact field's key (`lastName`, `city`, `birthday`, … or a custom field's id) and
 * fills the form field bound to it (`SiteChatField.attr`). A key with no such field on the form fills nothing.
 */
export type SiteChatVisitor = { name?: string; email?: string; phone?: string } & Record<string, SiteChatVisitorValue | undefined>;

type PageKey = keyof SiteChatPageSettings;

/** One change: a value sets the key, `null` gives it back to the channel's settings. */
export type SiteChatPagePatch = {
    [K in PageKey]?: (K extends 'colors' ? { [C in keyof SiteChatColors]?: string | null } : SiteChatPageSettings[K]) | null;
};

export type ParsedPageInput = {
    patch: SiteChatPagePatch;
    visitor?: SiteChatVisitor;
    data?: Record<string, unknown>;
    /** `contact` / `setContact`: contact fields by key as the server takes them (text; lists as JSON; yes/no as `true`/`false`). */
    contact?: Record<string, string>;
    /** `identity` / `identify`: the signed visitor token; null forgets it. */
    identity?: string | null;
};

const FLAGS = ['launcher', 'openOnLoad', 'skipHome', 'emoji'] as const;
/** `false` switches the channel's setting off; `true` gives it back. */
const OFF_ONLY = ['voice', 'rules', 'triggers'] as const;
const TEXTS = ['title', 'subtitleOnline', 'subtitleOffline', 'greeting', 'cardText', 'composerPlaceholder'] as const;
const NUMBERS: Record<'offsetX' | 'offsetY' | 'width' | 'zIndex', [number, number]> = {
    offsetX: [0, 1000],
    offsetY: [0, 1000],
    width: [320, 640],
    zIndex: [0, 2147483647],
};
const COLOR_KEYS: (keyof SiteChatColors)[] = ['button', 'header', 'guestBubble', 'guestText', 'operatorBubble', 'operatorText'];

/** Every key `AXLChatSettings` / `configure` understands, for the docs and the warning about a mistyped one. */
export const SITE_CHAT_PAGE_KEYS: string[] = [
    ...FLAGS, ...OFF_ONLY, ...TEXTS, ...Object.keys(NUMBERS),
    'position', 'theme', 'lang', 'colors', 'logo', 'visitor', 'data', 'contact', 'identity',
];

/**
 * `setContact`'s values as the server takes them: text and numbers as text, yes/no as `true`/`false`, several list
 * options as a JSON array, a date as `YYYY-MM-DD`; an empty value takes the key back. Anything else is reported and skipped.
 */
export function parseContactFields(raw: unknown): Record<string, string> | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        warn('contact fields must be an object');
        return null;
    }
    const result: Record<string, string> = {};
    for (const [rawKey, value] of Object.entries(raw as Record<string, unknown>).slice(0, 50)) {
        const key = rawKey.trim().slice(0, 100);
        if (!key)
            continue;
        if (value == null || value === '')
            result[key] = '';
        else if (typeof value === 'string')
            result[key] = value.trim().slice(0, 1000);
        else if (typeof value === 'number' && Number.isFinite(value))
            result[key] = String(value);
        else if (typeof value === 'boolean')
            result[key] = value ? 'true' : 'false';
        else if (value instanceof Date && !Number.isNaN(value.getTime()))
            result[key] = localDateTime(value).slice(0, 10);
        else if (Array.isArray(value))
            result[key] = JSON.stringify(value.filter(item => typeof item === 'string' || typeof item === 'number').map(String).slice(0, 50));
        else
            warn(`invalid value of «contact.${key}» — ignored`);
    }
    return result;
}

const warn = (text: string) => console.warn(`[AXLChat] ${text}`);

/** `en`, `EN`, `en-US`, `zh-TW`, `zhtw` → a widget language; anything else → null. */
export function pageLang(value: unknown): SiteChatLang | null {
    if (typeof value !== 'string')
        return null;
    const tag = value.trim().toLowerCase().replace(/[-_]/g, '');
    if ((SITE_CHAT_LANGS as string[]).includes(tag))
        return tag as SiteChatLang;
    if (tag.startsWith('zh'))
        return tag.includes('tw') || tag.includes('hk') || tag.includes('hant') ? 'zhtw' : 'zhcn';
    const short = tag.slice(0, 2);
    return (SITE_CHAT_LANGS as string[]).includes(short) ? short as SiteChatLang : null;
}

function text(value: unknown, max: number): string | null {
    return typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : null;
}

/**
 * Checks what the page passed. A wrong value is reported in the console and skipped — the page's code must never break
 * the widget, and the widget must never break the page.
 */
export function parsePageSettings(raw: unknown): ParsedPageInput {
    const result: ParsedPageInput = { patch: {} };
    if (raw == null)
        return result;
    if (typeof raw !== 'object' || Array.isArray(raw)) {
        warn('settings must be an object');
        return result;
    }
    const patch = result.patch as Record<string, unknown>;
    const invalid = (key: string) => warn(`invalid value of «${key}» — ignored`);
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
        if (value === undefined)
            continue;
        if (key === 'visitor') {
            if (value && typeof value === 'object')
                result.visitor = parseVisitor(value);
            else if (value !== null)
                invalid(key);
            continue;
        }
        if (key === 'data') {
            if (value && typeof value === 'object' && !Array.isArray(value))
                result.data = value as Record<string, unknown>;
            else if (value !== null)
                invalid(key);
            continue;
        }
        if (key === 'contact') {
            const contact = value === null ? null : parseContactFields(value);
            if (contact)
                result.contact = contact;
            continue;
        }
        if (key === 'identity') {
            // A JWT is three base64url parts; anything else is a mistake worth a console line, not a request.
            if (value === null || typeof value === 'string' && /^[\w-]+\.[\w-]+\.[\w-]+$/.test(value.trim()))
                result.identity = value === null ? null : value.trim();
            else
                invalid(key);
            continue;
        }
        if (!SITE_CHAT_PAGE_KEYS.includes(key)) {
            warn(`unknown setting «${key}» — ignored`);
            continue;
        }
        if (value === null) {
            patch[key] = null;
            continue;
        }
        if ((FLAGS as readonly string[]).includes(key)) {
            if (typeof value === 'boolean')
                patch[key] = value;
            else
                invalid(key);
        } else if ((OFF_ONLY as readonly string[]).includes(key)) {
            if (typeof value === 'boolean')
                patch[key] = value ? null : false;
            else
                invalid(key);
        } else if ((TEXTS as readonly string[]).includes(key)) {
            // An empty text gives the channel's own back: a widget with no title is not a thing to configure.
            patch[key] = typeof value === 'string' ? text(value, 1000) : (invalid(key), undefined);
        } else if (key in NUMBERS) {
            const [min, max] = NUMBERS[key as keyof typeof NUMBERS];
            const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
            if (Number.isFinite(number))
                patch[key] = Math.min(max, Math.max(min, Math.round(number)));
            else
                invalid(key);
        } else if (key === 'position') {
            if (value === 'left' || value === 'right')
                patch[key] = value;
            else
                invalid(key);
        } else if (key === 'theme') {
            if (value === 'light' || value === 'dark' || value === 'system')
                patch[key] = value;
            else
                invalid(key);
        } else if (key === 'lang') {
            const lang = pageLang(value);
            if (lang)
                patch[key] = lang;
            else
                invalid(key);
        } else if (key === 'logo') {
            if (typeof value === 'string' && /^(https?:)?\/\//i.test(value.trim()))
                patch[key] = value.trim();
            else
                invalid(key);
        } else if (key === 'colors') {
            if (typeof value !== 'object' || Array.isArray(value)) {
                invalid(key);
                continue;
            }
            const colors: Record<string, string | null> = {};
            for (const [name, color] of Object.entries(value as Record<string, unknown>)) {
                if (!(COLOR_KEYS as string[]).includes(name))
                    warn(`unknown colour «${name}» — ignored`);
                else if (color === null || color === '')
                    colors[name] = null;
                else if (typeof color === 'string' && color.trim().length <= 100)
                    colors[name] = color.trim();
                else
                    invalid(`colors.${name}`);
            }
            patch[key] = colors;
        }
    }
    for (const key of Object.keys(patch))
        if (patch[key] === undefined)
            delete patch[key];
    return result;
}

function parseVisitor(raw: object): SiteChatVisitor {
    const visitor: SiteChatVisitor = {};
    for (const [rawKey, value] of Object.entries(raw).slice(0, 50)) {
        const key = rawKey.trim().slice(0, 100);
        if (!key || value == null)
            continue;
        if (typeof value === 'string')
            visitor[key] = value.trim().slice(0, 1000);
        // The form keeps text: a number (a phone, an age) is its digits.
        else if (typeof value === 'number' && Number.isFinite(value))
            visitor[key] = String(value);
        else if (typeof value === 'boolean')
            visitor[key] = value;
        else if (value instanceof Date && !Number.isNaN(value.getTime()))
            visitor[key] = localDateTime(value);
        else if (Array.isArray(value))
            visitor[key] = value.filter(item => typeof item === 'string' || typeof item === 'number').map(String).slice(0, 50);
        else
            warn(`invalid value of «visitor.${key}» — ignored`);
    }
    return visitor;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DDTHH:mm` in the visitor's own time, the shape of the form's date-and-time field. */
function localDateTime(date: Date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * The form's own value for one `setVisitor` value, by the field's kind: yes/no `'true'` or empty, a date `YYYY-MM-DD`,
 * date and time `YYYY-MM-DDTHH:mm`, a list option by its id or its text, several options as a JSON array of ids.
 * null = the value does not fit the field (an unknown option, not a date) and is skipped.
 */
export function visitorFormValue(field: Pick<SiteChatField, 'kind' | 'options'>, value: SiteChatVisitorValue): string | null {
    const option = (item: string) => {
        const text = item.trim().toLowerCase();
        return (field.options ?? []).find(o => o.id === item.trim() || o.value.trim().toLowerCase() === text)?.id ?? null;
    };
    switch (field.kind) {
        case 'boolean':
            return value === true || /^(true|1|yes)$/i.test(String(value).trim()) ? 'true' : '';
        case 'date':
            return /^\d{4}-\d{2}-\d{2}/.test(String(value)) ? String(value).slice(0, 10) : null;
        case 'dateTime': {
            const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(String(value));
            return match ? `${match[1]}T${match[2]}` : null;
        }
        case 'list':
            return option(Array.isArray(value) ? value[0] ?? '' : String(value));
        case 'multiList': {
            const items = Array.isArray(value) ? value : String(value).split(',');
            const ids = [...new Set(items.map(option).filter((id): id is string => !!id))];
            return ids.length ? JSON.stringify(ids) : null;
        }
        case 'number':
            return Number.isFinite(Number(String(value).replace(',', '.'))) ? String(value) : null;
        default:
            return Array.isArray(value) ? value.join(', ') : String(value);
    }
}

/** The page's settings after one more change; `null` keys are dropped, colours are merged one by one. */
export function mergePageSettings(current: SiteChatPageSettings, patch: SiteChatPagePatch): SiteChatPageSettings {
    const next: Record<string, unknown> = { ...current };
    for (const [key, value] of Object.entries(patch)) {
        if (key === 'colors' && value) {
            const colors: Record<string, string> = { ...(current.colors ?? {}) };
            for (const [name, color] of Object.entries(value as Record<string, string | null>)) {
                if (color == null)
                    delete colors[name];
                else
                    colors[name] = color;
            }
            if (Object.keys(colors).length)
                next.colors = colors;
            else
                delete next.colors;
        } else if (value === null)
            delete next[key];
        else
            next[key] = value;
    }
    return next as SiteChatPageSettings;
}

/** The channel's config as this page shows it. Page-only keys (`launcher`, `openOnLoad`, `zIndex`) stay out of it. */
export function applyPageSettings(config: SiteChatConfig, page: SiteChatPageSettings): SiteChatConfig {
    const next: SiteChatConfig = page.lang && page.lang !== config.lang ? switchSiteChatLanguage(config, page.lang) : { ...config };
    if (page.position)
        next.position = page.position;
    if (page.offsetX != null)
        next.offsetX = page.offsetX;
    if (page.offsetY != null)
        next.offsetY = page.offsetY;
    if (page.width != null)
        next.width = page.width;
    if (page.theme)
        next.theme = page.theme;
    if (page.colors)
        next.colors = { ...next.colors, ...page.colors };
    if (page.logo)
        next.logo = page.logo;
    for (const key of TEXTS) {
        if (page[key])
            next[key] = page[key]!;
    }
    if (page.skipHome != null)
        next.skipHome = page.skipHome;
    if (page.emoji != null)
        next.emoji = page.emoji;
    if (page.voice === false)
        next.voice = false;
    if (page.rules === false) {
        next.rules = [];
        next.mobileOnly = false;
        next.hideMobile = false;
    }
    if (page.triggers === false)
        next.triggers = [];
    return next;
}
